import logger from '../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  HandHeart, 
  Users, 
  Globe, 
  ShieldCheck, 
  ArrowRight,
  ChevronDown,
  Package,
  Utensils,
  Sparkles,
  DollarSign,
  TrendingUp,
  ImageIcon
} from 'lucide-react';
import axios from 'axios';

import GlobalCandleWall from '../components/GlobalCandleWall';
import SectionMedia from '../components/SectionMedia';
import { CommunityUpdatesPreview } from './components/CommunityUpdatesPreview';
import { HomeTestimonials } from './components/HomeTestimonials';
import { ShareStorySection } from './components/ShareStorySection';
import CommunitySuggestionsSection from './components/CommunitySuggestionsSection';
import FeaturedMerchSection from '../components/FeaturedMerchSection';
import { useSiteContent } from '../hooks/useSiteContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated counter hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  const startAnimation = useCallback(() => {
    if (hasStarted || end === 0) return;
    setHasStarted(true);
    const startTime = performance.now();
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  useEffect(() => {
    if (!startOnView || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) startAnimation(); },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView, startAnimation]);

  return { count, ref };
};

// Mini Sparkline SVG — responsive width
const Sparkline = ({ data, color = '#BA1826' }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const h = 28;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${h - (v / max) * (h - 4) - 2}`).join(' ');
  const areaPoints = `0,${h} ${points} 100,${h}`;
  return (
    <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" className="w-full max-w-[100px] md:max-w-[120px] h-7 mx-auto mt-2 opacity-70">
      <polyline points={areaPoints} fill={color} fillOpacity="0.15" stroke="none" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

// Animated Impact Counters component
const AnimatedStat = ({ icon: Icon, value, prefix = '', suffix = '', label, loading, sparkData, isCurrency = false, testId }) => {
  const { count, ref } = useCountUp(Math.round(value || 0), 2000);
  let displayValue;
  if (isCurrency) {
    // Currency: show full dollar amount with commas (no k suffix under $10k; k suffix above)
    if (value >= 10000) {
      displayValue = prefix + `${(count / 1000).toFixed(count >= value ? 1 : 0)}k`;
    } else {
      displayValue = prefix + count.toLocaleString() + suffix;
    }
  } else {
    displayValue = prefix + (value >= 1000 ? `${(count / 1000).toFixed(count >= value ? 1 : 0)}k` : count.toString()) + suffix;
  }

  return (
    <div className="text-center p-3 md:p-4 bg-white/5 rounded-lg" ref={ref} data-testid={testId}>
      <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#BA1826] mx-auto mb-1 md:mb-2" />
      <div className="stat-number text-2xl md:text-3xl lg:text-4xl mb-1" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {loading ? '—' : displayValue}
      </div>
      <span className="text-gray-400 text-[10px] md:text-xs leading-tight block">{label}</span>
      {sparkData && sparkData.length > 0 && (
        <div className="group/spark relative" title="Shows donation activity over the last 30 days">
          <Sparkline data={sparkData} />
          <span className="text-gray-500 text-[8px] md:text-[9px] mt-0.5 block">30-day trend</span>
        </div>
      )}
    </div>
  );
};

const ImpactCounters = ({ loading, counter, trend }) => (
  <section className="bg-[#0A0A0A] py-10" data-testid="impact-counter-section">
    <div className="px-6">
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-1">
          Live Impact
        </p>
        <h2 className="font-heading text-xl text-white font-bold">
          Your Donations at Work
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <AnimatedStat
          icon={Users}
          value={counter.families_supported}
          label="Families Supported"
          loading={loading}
          sparkData={trend?.map(d => d.families)}
          testId="counter-families-supported"
        />
        <AnimatedStat
          icon={DollarSign}
          value={counter.total_donated}
          prefix="$"
          label="Total Donated"
          loading={loading}
          sparkData={trend?.map(d => d.amount)}
          isCurrency
          testId="counter-total-donated"
        />
        <AnimatedStat
          icon={Package}
          value={counter.buckets_delivered}
          label="Buckets Delivered"
          loading={loading}
          testId="counter-buckets-delivered"
        />
        <AnimatedStat
          icon={Sparkles}
          value={counter.hygiene_kits_delivered}
          label="Hygiene Kits Delivered"
          loading={loading}
          testId="counter-hygiene-kits-delivered"
        />
      </div>

      <p className="text-center text-xs text-gray-500 mt-4">
        Totals reflect completed donations only — pending, failed, and refunded payments are excluded.
      </p>
    </div>
  </section>
);

const Home = () => {
  const [impactCounter, setImpactCounter] = useState({
    families_supported: 0,
    total_donated: 0,
    buckets_delivered: 0,
    hygiene_kits_delivered: 0,
  });
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [storyForm, setStoryForm] = useState({ name: '', quote: '', country: '' });
  const [storySubmitted, setStorySubmitted] = useState(false);
  const [storySubmitting, setStorySubmitting] = useState(false);
  const [siteImages, setSiteImages] = useState({});

  const { data: hero } = useSiteContent('home_hero', {
    eyebrow: 'Novena Humanitarian Foundation',
    title: 'Delivering essentials. Restoring dignity.',
    title_highlight: 'Building trust.',
    subtitle_primary: 'Through compassion, integrity, and full transparency.',
    subtitle_secondary: 'We aim to support individuals and families facing hardship with food, emergency aid, and essential supplies, delivered with dignity, compassion, and complete transparency.',
    cta_primary_label: 'Make an Impact',
    cta_primary_url: '/donate',
    cta_secondary_label: 'Our Mission',
    cta_secondary_url: '/about',
  });
  const { data: ctaContent } = useSiteContent('home_cta', {
    headline: 'Ready to Make a Difference?',
    subtitle: 'Your support helps provide food, hygiene essentials, emergency care, and life-saving resources to families in need.',
    cta_primary_label: 'Donate Now',
    cta_primary_url: '/donate',
    cta_secondary_label: 'Get Involved',
    cta_secondary_url: '/involved',
  });

  const fetchSiteImages = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/site-images`);
      setSiteImages(res.data.images || {});
    } catch (e) { logger.error(e); }
  }, []);

  const fetchRecentUpdates = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/updates/recent`);
      setRecentUpdates(response.data);
    } catch (error) {
      logger.error('Error fetching recent updates:', error);
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      logger.error('Error fetching testimonials:', error);
    }
  }, []);

  const fetchImpactStats = useCallback(async () => {
    try {
      const [counterRes, trendRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/impact-counter`),
        axios.get(`${BACKEND_URL}/api/public/impact-stats`),
      ]);
      setImpactCounter(counterRes.data);
      setTrend(trendRes.data?.trend || []);
    } catch (error) {
      logger.error('Error fetching impact stats:', error);
      setImpactCounter({
        families_supported: 0,
        total_donated: 0,
        buckets_delivered: 0,
        hygiene_kits_delivered: 0,
      });
      setTrend([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImpactStats();
    fetchTestimonials();
    fetchRecentUpdates();
    fetchSiteImages();
  }, [fetchImpactStats, fetchTestimonials, fetchRecentUpdates, fetchSiteImages]);

  const submitStory = async () => {
    if (!storyForm.name.trim() || !storyForm.quote.trim()) return;
    setStorySubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/testimonials/submit`, storyForm);
      setStorySubmitted(true);
      setStoryForm({ name: '', quote: '', country: '' });
    } catch (error) {
      logger.error('Error submitting story:', error);
    } finally {
      setStorySubmitting(false);
    }
  };

  const values = [
    {
      icon: ShieldCheck,
      title: 'Dignity First',
      description: 'Every person we serve is treated with respect and privacy.',
    },
    {
      icon: Sparkles,
      title: 'Transparency',
      description: 'Donors see exactly where their support goes.',
    },
    {
      icon: HandHeart,
      title: 'Compassion',
      description: 'We meet people where they are, without judgment.',
    },
    {
      icon: Globe,
      title: 'Community',
      description: 'Starting locally in New York, growing globally.',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="relative min-h-[85vh] flex items-center"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 z-0">
          <div className="w-full h-full bg-[#F8F9FA] flex items-center justify-center">
            <div className="text-center px-6 opacity-30">
              <ImageIcon className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#94A3B8]">Add brand/mission visual here</p>
              <p className="text-xs text-[#CBD5E1] mt-1">Use a clean branded image, event flyer, or mission-based visual</p>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70"></div>
        </div>

        <div className="relative z-10 px-6 py-12">
          <div className="max-w-lg">
            <p className="text-sm uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-3 animate-fade-in">
              <span className="text-[#BA1826]">{hero.eyebrow}</span>
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] leading-tight mb-4 animate-fade-in animate-delay-100">
              {hero.title}{hero.title_highlight ? ' ' : ''}
              {hero.title_highlight && <span className="text-[#BA1826]">{hero.title_highlight}</span>}
            </h1>
            {hero.subtitle_primary && (
              <p className="text-base font-medium text-[#0A0A0A] leading-relaxed mb-2 animate-fade-in animate-delay-200">
                {hero.subtitle_primary}
              </p>
            )}
            {hero.subtitle_secondary && (
              <p className="text-base text-[#4B5563] leading-relaxed mb-6 animate-fade-in animate-delay-200">
                {hero.subtitle_secondary}
              </p>
            )}
            <div className="flex flex-wrap gap-3 animate-fade-in animate-delay-300">
              <Link to={hero.cta_primary_url || '/donate'} className="btn-primary text-sm" data-testid="hero-donate-btn">
                {hero.cta_primary_label}
                <ArrowRight className="w-4 h-4" />
              </Link>
              {hero.cta_secondary_label && (
                <Link to={hero.cta_secondary_url || '/about'} className="btn-secondary text-sm" data-testid="hero-mission-btn">
                  {hero.cta_secondary_label}
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 scroll-indicator">
          <ChevronDown className="w-5 h-5 text-[#4B5563]" />
        </div>
      </section>

      {/* Impact Counter Section */}
      <ImpactCounters loading={loading} counter={impactCounter} trend={trend} />

      {/* Global Candle Wall */}
      <GlobalCandleWall maxDisplay={24} />

      {/* Mission Section */}
      <section className="py-12 bg-white" data-testid="mission-section">
        <div className="px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">
                Our Mission
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
                Because no one should have to choose between dignity and survival.
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-3 text-sm">
                Novena Humanitarian Foundation supports individuals and families facing hardship, both locally and abroad.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-3 text-sm">
                By providing essential resources such as food, toiletries, hygiene products, and emergency assistance, we help meet everyday needs with care and respect.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-3 text-sm">
                We believe no one should feel forgotten or ashamed during difficult times.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-3 text-sm">
                Our work is rooted in service, offering immediate relief while creating a path toward long-term stability.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-6 text-sm">
                Our goal is simple: to provide support today and help build a stronger, more stable tomorrow.
              </p>
              <Link to="/about" className="btn-secondary text-sm" data-testid="learn-more-btn">
                Learn More About Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <SectionMedia
                sectionKey="home-mission"
                className="w-full aspect-[4/3] rounded-sm"
                fallback={siteImages.home_mission
                  ? `${BACKEND_URL}/api/uploads/${siteImages.home_mission}`
                  : null}
                priority
              />
              <div className="absolute -bottom-4 -left-4 bg-[#BA1826] text-white p-4 max-w-[200px]">
                <p className="font-heading text-base font-semibold">
                  Helping without compromising dignity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-12 bg-[#F8F9FA]" data-testid="services-section">
        <div className="px-6">
          <div className="text-center max-w-lg mx-auto mb-10">
            <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">
              What We Do
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">
              Essential Humanitarian Support
            </h2>
            <p className="text-[#4B5563] text-sm">
              We are working to provide hands-on, dignity-first services that prioritize respect and real impact.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Utensils, title: 'Food Assistance', desc: 'Nutritious meals and groceries for families' },
              { icon: Package, title: 'Hygiene Supplies', desc: 'Toiletries and hygiene products' },
              { icon: Heart, title: 'Emergency Aid', desc: 'Immediate support during crisis situations' },
              { icon: HandHeart, title: 'Daily Necessities', desc: 'Essential items like food staples, hygiene products, and basic household needs.' },
            ].map((service) => (
              <div
                key={service.title}
                className="bg-white p-5 border border-[#E5E7EB] card-hover"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <service.icon className="w-8 h-8 text-[#BA1826] mb-3" />
                <h3 className="font-heading text-base font-semibold mb-1">{service.title}</h3>
                <p className="text-[#4B5563] text-xs">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 bg-white" data-testid="values-section">
        <div className="px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">
                Our Values
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
                Our Values Guide Everything We Do
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-6 text-sm">
                Many organizations provide aid, but too often transparency comes at the cost 
                of privacy, or visibility outweighs respect. Novena was created to ensure that 
                donors clearly understand where support goes, while individuals and families 
                are helped without exploitation or loss of dignity.
              </p>

              <div className="space-y-4">
                {values.map((value) => (
                  <div key={value.title} className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#FEE2E2] rounded flex items-center justify-center">
                      <value.icon className="w-5 h-5 text-[#BA1826]" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-base mb-1">{value.title}</h4>
                      <p className="text-[#4B5563] text-xs">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <SectionMedia
                sectionKey="home-values"
                className="w-full aspect-[4/3] rounded-sm"
                fallback={siteImages.home_values
                  ? `${BACKEND_URL}/api/uploads/${siteImages.home_values}`
                  : null}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#BA1826]" data-testid="cta-section">
        <div className="px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
            {ctaContent.headline}
          </h2>
          <p className="text-white/80 max-w-md mx-auto mb-6 text-sm">
            {ctaContent.subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to={ctaContent.cta_primary_url || '/donate'}
              className="bg-white text-[#BA1826] px-6 py-2 font-semibold rounded hover:bg-gray-100 transition-all text-sm"
              data-testid="cta-donate-btn"
            >
              {ctaContent.cta_primary_label}
            </Link>
            {ctaContent.cta_secondary_label && (
              <Link
                to={ctaContent.cta_secondary_url || '/involved'}
                className="bg-transparent text-white border-2 border-white px-6 py-2 font-semibold rounded hover:bg-white/10 transition-all text-sm"
                data-testid="cta-involved-btn"
              >
                {ctaContent.cta_secondary_label}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-white" data-testid="how-it-works-section">
        <div className="px-6">
          <div className="text-center mb-8">
            <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-2">
              Simple & Transparent
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { step: '1', title: 'Choose a Program', desc: 'Select a local or international program to support.' },
              { step: '2', title: 'Support a Family', desc: 'Pick a donation tier that fits your budget.' },
              { step: '3', title: 'We Deliver Directly', desc: 'Your support goes straight to families in need.' },
              { step: '4', title: 'You See the Impact', desc: 'Track progress and see real results from your giving.' },
            ].map((item) => (
              <div key={item.step} className="text-center p-4" data-testid={`how-step-${item.step}`}>
                <div className="w-10 h-10 bg-[#BA1826] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold text-sm">{item.step}</span>
                </div>
                <h4 className="font-heading font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-[#4B5563]">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Social Proof */}
          <p className="text-center text-xs text-[#4B5563] mt-8 max-w-md mx-auto" data-testid="social-proof">
            Serving communities locally in New York and expanding across multiple countries.
          </p>
        </div>
      </section>

      {/* Where We Work */}
      <section className="py-12 bg-[#F8F9FA]" data-testid="location-section">
        <div className="px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <SectionMedia
                sectionKey="home-location"
                className="w-full aspect-[4/3] rounded-sm"
                fallback={siteImages.home_location
                  ? `${BACKEND_URL}/api/uploads/${siteImages.home_location}`
                  : null}
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">
                Where We Work
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
                Starting Local, Growing Global
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-6 text-sm">
                We are building programs to serve communities locally in New York and across multiple countries through structured, transparent support programs.
              </p>
              <Link to="/international" className="btn-primary text-sm" data-testid="projects-btn">
                View Our Reach
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Community Updates Preview */}
      <CommunityUpdatesPreview recentUpdates={recentUpdates} />

      {/* Featured Merchandise — Support Our Mission */}
      <FeaturedMerchSection />

      {/* Testimonials / Success Stories */}
      <HomeTestimonials testimonials={testimonials} />

      {/* Share Your Story */}
      <ShareStorySection
        storyForm={storyForm}
        setStoryForm={setStoryForm}
        storySubmitted={storySubmitted}
        setStorySubmitted={setStorySubmitted}
        storySubmitting={storySubmitting}
        submitStory={submitStory}
      />

      {/* Community Suggestions & Feedback */}
      <CommunitySuggestionsSection />
    </div>
  );
};

export default Home;
